import React from 'react';
import {
  AppBar,
  Box,
  Drawer,
  Grid,
  IconButton,
  Modal,
  Stack,
  Switch,
  Toolbar,
  Tooltip
} from '@mui/material';
import PropTypes from 'prop-types';
import { styled, useTheme } from '@mui/material/styles';
import { isEqual, isEmpty } from 'lodash';

import AddIcon from '@mui/icons-material/Add';
import AddLinkIcon from '@mui/icons-material/AddLink';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import DescriptionIcon from '@mui/icons-material/Description';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import ImageIcon from '@mui/icons-material/Image';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LayersIcon from '@mui/icons-material/Layers';
import MapIcon from '@mui/icons-material/Map';
import RemoveIcon from '@mui/icons-material/Remove';
import ShareIcon from '@mui/icons-material/Share';
import StorageIcon from '@mui/icons-material/Storage';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import AnnotationDirectButtonsComponent from '../AnnotationDirectButtonsComponent';
import FileToolsComponent from '../FileToolsComponent';
import InfoLayerSelection from '../InfoLayerSelection';
import IntroductionMapComponent from '../IntroductionMapComponent';
import ISLComponent from '../ISLComponent';
// import LeafletFunctionalities from '../LeafletFunctionalities';
import DownloadLinksComponent from '../DownloadLinksComponent';
import MapLayerOrder from '../MapLayerOrder';
import MapShareTogether from '../MapShareTogether';
import NotesComponent from '../NotesComponent';
import SentinelComponent from '../SentinelComponent';
import ShowMapDeckLidarComponent from '../ShowMapDeckLidarComponent';
import PointMapComponent from '../PointMapComponent';
import ViewLinksComponent from '../ViewLinksComponent';

import useTimeStore from '../ZustandProvider';
import { useStyles } from '../../../styles/mapStylesCustom';

const BotonMenu = props => {
  const { changeLoading, isMobile, isPublicMap, map, MapProps } = props;
  const { settingsAdvancedMap } = map;

  const adjustMap = useTimeStore(state => state.adjustMap);
  const drawerWidth = useTimeStore(state => state.drawerWidth);
  const layers = useTimeStore(state => state.layers);
  const setAdjustMap = useTimeStore(state => state.setAdjustMap);
  const setDrawerWidth = useTimeStore(state => state.setDrawerWidth);
  const setGeometryConfig = useTimeStore(state => state.setGeometryConfig);

  const openLeftStart = drawerWidth !== 0;

  const [componentToRender, setComponentToRender] = React.useState('');
  const drawerWidthLeft =
    drawerWidth === 0 ? `${100 / 3 - 1 / 3}vw` : `${drawerWidth - drawerWidth / 100}vw`;
  const drawerWidthLocal = isMobile ? '100%' : drawerWidthLeft;
  const [elementToRenderAux, setElementToRenderAux] = React.useState(
    settingsAdvancedMap.defaultComponent
  );
  const [expandDrawerMobile, setExpandDrawerMobile] = React.useState(false);
  const [featureProp, setFeatureProp] = React.useState({});
  const [openLeft, setOpenLeft] = React.useState(openLeftStart);
  const [tiempoEspera, setTiempoEspera] = React.useState(false);
  const [checkAdjustMap, setCheckAdjustMap] = React.useState(true);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const heightLeft = isPublicMap ? 0 : '56px';
  const heightMobile = openLeft ? heightLeft : '60%';
  const heightLocal = isMobile ? heightMobile : heightLeft;

  const theme = useTheme();
  const classes = useStyles();

  const marks = [
    {
      value: 25
    },
    {
      value: 100 / 3
    },
    {
      value: 50
    },
    {
      value: 75
    },
    {
      value: 100
    }
  ].sort((a, b) => a.value - b.value);

  const AppBarStyled = styled(AppBar, {
    shouldForwardProp: prop => prop !== 'open'
  })(({ open }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    marginLeft: 0,
    ...(open && {
      marginLeft: `${drawerWidthLocal}px`,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen
      })
    })
  }));

  const DrawerHeader = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'center'
  }));

  document.getElementsByTagName('body')[0].onclick = () => {
    const property = JSON.parse(localStorage.getItem('Propperties'));
    if (!isEqual(property, featureProp) && property !== null) {
      setFeatureProp({ ...property });
    }
    localStorage.removeItem('Propperties');
  };

  const tiempoEsperaFunction = () => {
    setTiempoEspera(true);
    setTimeout(() => {
      setTiempoEspera(false);
    }, 500);
  };

  const handleDrawerBottom = () => {
    setIsModalOpen(false);
    if (drawerWidth === 0) setDrawerWidth(100 / 3);
    if (openLeft === true) {
      setOpenLeft(false);
      setAdjustMap(false);
    } else {
      setOpenLeft(true);
      if (checkAdjustMap === true) {
        setAdjustMap(true);
      }
    }
  };

  const handleIncrement = () => {
    const currentIndex = marks.findIndex(mark => mark.value === drawerWidth);
    if (currentIndex < marks.length - 1) {
      setDrawerWidth(marks[currentIndex + 1].value);
    }
  };

  const handleDecrement = () => {
    const currentIndex = marks.findIndex(mark => mark.value === drawerWidth);
    if (currentIndex > 0) {
      setDrawerWidth(marks[currentIndex - 1].value);
    }
  };

  const renderComponent = async component => {
    switch (component) {
      case 'AnnotationDirectButtons':
        setComponentToRender(
          <AnnotationDirectButtonsComponent isMobile={isMobile} MapProps={MapProps} />
        );
        break;
      case 'DownloadLinksComponent':
        setComponentToRender(<DownloadLinksComponent isMobile={isMobile} MapProps={MapProps} />);
        break;
      case 'FileToolsComponent':
        setComponentToRender(<FileToolsComponent />);
        break;
      case 'ISLComponent':
        setComponentToRender(
          <ISLComponent MapProps={MapProps} isMobile={isMobile} isFullScreen={isModalOpen} />
        );
        break;
      case 'InfoLayerSelection':
        setComponentToRender(<InfoLayerSelection featureProp={featureProp} map={{ ...map }} />);
        break;
      case 'IntroductionMapComponent':
        setComponentToRender(
          <IntroductionMapComponent MapProps={MapProps} changeLoading={changeLoading} />
        );
        break;
      case 'LayersControl':
        setComponentToRender(
          <MapLayerOrder MapProps={MapProps} isPublicMap={isPublicMap} isMobile={isMobile} />
        );
        break;
      case 'NotesComponent':
        setComponentToRender(
          <NotesComponent
            isAnnotationsMaps
            isMobile={isMobile}
            video={settingsAdvancedMap.videos}
            notesProps={settingsAdvancedMap.notes}
          />
        );
        break;
      case 'SharedComponent':
        setComponentToRender(<MapShareTogether map={{ ...map }} isPublicMap={isPublicMap} />);
        break;
      case 'SentinelComponent':
        setComponentToRender(<SentinelComponent MapProps={MapProps} />);
        break;
      case 'ShowMapDeckLidarComponent':
        setComponentToRender(<ShowMapDeckLidarComponent />);
        break;
      case 'PointMapComponent':
        setComponentToRender(<PointMapComponent MapProps={MapProps} />);
        break;
      case 'ViewLinksComponent':
        setComponentToRender(<ViewLinksComponent isPublicMap={isPublicMap} MapProps={MapProps} />);
        break;
      default:
        setComponentToRender(false);
        break;
    }
    setElementToRenderAux(component);
  };

  const handleButtonAction = (component, geometryConfig) => {
    tiempoEsperaFunction();
    renderComponent(component);
    setGeometryConfig(geometryConfig);
  };

  const renderButtons = () => {
    const buttonData = [
      {
        icon: <RemoveIcon />,
        action: () => handleDecrement(),
        show: isMobile !== true || drawerWidth === 25 || isModalOpen === true,
        tooltipTitle: 'Disminuir tamaño'
      },
      {
        icon: <AddIcon />,
        action: () => handleIncrement(),
        show: isMobile !== true || drawerWidth === 100 || isModalOpen === true,
        tooltipTitle: 'Aumentar tamaño'
      },
      {
        icon: <EditLocationAltIcon />,
        action: () => handleButtonAction('AnnotationDirectButtons', 'component'),
        show:
          settingsAdvancedMap.components.AnnotationDirectButtons &&
          settingsAdvancedMap.components.AnnotationDirectButtons.show,
        tooltipTitle:
          settingsAdvancedMap.components.AnnotationDirectButtons &&
          settingsAdvancedMap.components.AnnotationDirectButtons.name
      },
      {
        icon: <DriveFolderUploadOutlinedIcon />,
        action: () => handleButtonAction('FileToolsComponent', 'component'),
        show:
          settingsAdvancedMap.components.FileToolsComponent &&
          settingsAdvancedMap.components.FileToolsComponent.show,
        tooltipTitle:
          settingsAdvancedMap.components.FileToolsComponent &&
          settingsAdvancedMap.components.FileToolsComponent.name
      },
      {
        icon: <CoPresentIcon />,
        action: () => handleButtonAction('IntroductionMapComponent', 'component'),
        show:
          settingsAdvancedMap.components.IntroductionMapComponent &&
          settingsAdvancedMap.components.IntroductionMapComponent.show,
        tooltipTitle:
          settingsAdvancedMap.components.IntroductionMapComponent &&
          settingsAdvancedMap.components.IntroductionMapComponent.name
      },
      {
        icon: <StorageIcon />,
        action: () => handleButtonAction('ISLComponent', 'component'),
        show:
          settingsAdvancedMap.components.ISLComponent &&
          settingsAdvancedMap.components.ISLComponent.show,
        tooltipTitle:
          settingsAdvancedMap.components.ISLComponent &&
          settingsAdvancedMap.components.ISLComponent.name
      },
      {
        icon: <AddLinkIcon />,
        action: () => handleButtonAction('DownloadLinksComponent', ''),
        show:
          settingsAdvancedMap.components.DownloadLinksComponent &&
          settingsAdvancedMap.components.DownloadLinksComponent.show,
        tooltipTitle:
          settingsAdvancedMap.components.DownloadLinksComponent &&
          settingsAdvancedMap.components.DownloadLinksComponent.name
      },
      {
        icon: <InfoOutlinedIcon />,
        action: () => handleButtonAction('InfoLayerSelection', 'component'),
        show:
          settingsAdvancedMap.components.InfoLayerSelection &&
          settingsAdvancedMap.components.InfoLayerSelection.show,
        tooltipTitle:
          settingsAdvancedMap.components.InfoLayerSelection &&
          settingsAdvancedMap.components.InfoLayerSelection.name
      },
      {
        icon: <LayersIcon />,
        action: () => handleButtonAction('LayersControl', 'component'),
        show:
          settingsAdvancedMap.components.LayersControl &&
          settingsAdvancedMap.components.LayersControl.show,
        tooltipTitle:
          settingsAdvancedMap.components.LayersControl &&
          settingsAdvancedMap.components.LayersControl.name
      },
      {
        icon: <TextSnippetOutlinedIcon />,
        action: () => handleButtonAction('NotesComponent', 'component'),
        show:
          settingsAdvancedMap.components.NotesComponent &&
          settingsAdvancedMap.components.NotesComponent.show,
        tooltipTitle:
          settingsAdvancedMap.components.NotesComponent &&
          settingsAdvancedMap.components.NotesComponent.name
      },
      {
        icon: <ImageIcon />,
        action: () => handleButtonAction('SentinelComponent', 'sentinel'),
        show:
          settingsAdvancedMap.components.SentinelComponent &&
          settingsAdvancedMap.components.SentinelComponent.show,
        tooltipTitle:
          settingsAdvancedMap.components.SentinelComponent &&
          settingsAdvancedMap.components.SentinelComponent.name
      },
      {
        icon: <ShareIcon />,
        action: () => handleButtonAction('SharedComponent', 'component'),
        show:
          settingsAdvancedMap.components.SharedComponent &&
          settingsAdvancedMap.components.SharedComponent.show,
        tooltipTitle:
          settingsAdvancedMap.components.SharedComponent &&
          settingsAdvancedMap.components.SharedComponent.name
      },
      {
        icon: <MapIcon />,
        action: () => handleButtonAction('ShowMapDeckLidarComponent', 'component'),
        show:
          settingsAdvancedMap.components.ShowMapDeckLidarComponent &&
          settingsAdvancedMap.components.ShowMapDeckLidarComponent.show,
        tooltipTitle:
          settingsAdvancedMap.components.ShowMapDeckLidarComponent &&
          settingsAdvancedMap.components.ShowMapDeckLidarComponent.name
      },
      {
        icon: <TravelExploreIcon />,
        action: () => handleButtonAction('PointMapComponent', 'StreetView'),
        show:
          settingsAdvancedMap.components.PointMapComponent &&
          settingsAdvancedMap.components.PointMapComponent.show,
        tooltipTitle:
          settingsAdvancedMap.components.PointMapComponent &&
          settingsAdvancedMap.components.PointMapComponent.name
      },
      {
        icon: <DescriptionIcon />,
        action: () => handleButtonAction('ViewLinksComponent', 'component'),
        show:
          settingsAdvancedMap.components.ViewLinksComponent &&
          settingsAdvancedMap.components.ViewLinksComponent.show,
        tooltipTitle:
          settingsAdvancedMap.components.ViewLinksComponent &&
          settingsAdvancedMap.components.ViewLinksComponent.name
      }
    ];

    return buttonData.map(button => (
      <>
        {button.show && // Renderiza el botón si show es true
          (settingsAdvancedMap.showTooltip === true ? ( // Renderiza con Tooltip si tooltip es true
            <Tooltip title={button.tooltipTitle} arrow>
              <span>
                <IconButton
                  onClick={button.action}
                  disabled={tiempoEspera}
                  style={{ color: '#2A3F90', size: 'small' }}
                >
                  {button.icon}
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <IconButton // Renderiza sin Tooltip si tooltip es false
              onClick={button.action}
              disabled={tiempoEspera}
              style={{ color: '#2A3F90', size: 'small' }}
            >
              {button.icon}
            </IconButton>
          ))}
      </>
    ));
  };

  React.useEffect(() => {
    if (isEmpty(layers)) {
      setTimeout(() => {
        renderComponent(elementToRenderAux);
      }, 200);
    } else {
      renderComponent(elementToRenderAux);
    }
  }, [featureProp, map.shared, map.sharedPrivate]);

  return (
    <>
      <AppBarStyled
        position="relative"
        open={openLeft}
        style={{
          backgroundColor: 'rgb(251, 192, 45, 0)',
          color: 'black',
          radio: 3
        }}
        elevation={0}
      >
        <Toolbar disableGutters>
          <Stack direction="row" justifyContent="flex-start">
            {isMobile === false && openLeft === false && (
              <IconButton
                variant="contained"
                style={{
                  backgroundColor: 'white',
                  color: '#2A3F90',
                  border: '1px solid',
                  borderColor: '#2A3F90'
                }}
                size="small"
                aria-label="open drawer"
                onClick={handleDrawerBottom}
                edge="start"
              >
                <ChevronRightIcon />
              </IconButton>
            )}
            {isMobile === true && openLeft === false && (
              <IconButton
                variant="contained"
                style={{
                  backgroundColor: 'white',
                  color: '#2A3F90',
                  border: '1px solid',
                  borderColor: '#2A3F90'
                }}
                size="small"
                aria-label="open drawer"
                onClick={handleDrawerBottom}
                edge="start"
              >
                <KeyboardArrowUpIcon />
              </IconButton>
            )}
          </Stack>
        </Toolbar>
      </AppBarStyled>
      <Drawer
        sx={{
          width: drawerWidthLocal,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidthLocal,
            boxSizing: 'border-box',
            height: `calc(100% - ${heightLocal})`,
            top: isMobile === true && expandDrawerMobile === false ? '50%' : heightLocal
          }
        }}
        variant="persistent"
        anchor={isMobile ? 'bottom' : 'left'}
        open={openLeft}
      >
        <DrawerHeader>
          <Grid container width="90%" justifyContent="center">
            <Grid item xs={12}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fit, minmax(40px, 1fr))`,
                  gap: '8px',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {!isMobile && (
                  <Switch
                    checked={adjustMap}
                    onClick={() => {
                      setCheckAdjustMap(!checkAdjustMap);
                      setAdjustMap(!adjustMap);
                    }}
                    label={checkAdjustMap === true ? 'Ajustar' : 'Block'}
                    color="warning"
                  />
                )}
                {isMobile && (
                  <IconButton
                    style={{ color: '#2A3F90' }}
                    aria-label="open drawer"
                    onClick={() => setExpandDrawerMobile(!expandDrawerMobile)}
                    edge="start"
                  >
                    {expandDrawerMobile ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                  </IconButton>
                )}
                {renderButtons()}
                <IconButton
                  className={classes.iconButtonStyle}
                  onClick={() => setIsModalOpen(true)}
                >
                  <AspectRatioIcon />
                </IconButton>
                <IconButton className={classes.iconButtonStyle} onClick={handleDrawerBottom}>
                  <CloseIcon />
                </IconButton>
              </div>
            </Grid>
          </Grid>
        </DrawerHeader>
        {!isEmpty(componentToRender) ? (
          <Box
            bgcolor="white"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: isMobile === true && expandDrawerMobile === false ? '35%' : '100%',
              overflowY:
                isMobile || elementToRenderAux !== 'IntroductionMapComponent' ? 'auto' : 'hidden',
              overflowX: 'hidden'
            }}
          >
            <div className="container-fluid">{componentToRender}</div>
          </Box>
        ) : null}
      </Drawer>
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        style={{ position: 'absolute', bgcolor: 'white', top: '0%', left: '0%' }}
      >
        <Box backgroundColor="white" height="100%" width="100%">
          <Box
            height="5%"
            width="100%"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px'
            }}
          >
            {renderButtons()}
            <IconButton
              variant="outlined"
              onClick={() => {
                setIsModalOpen(false);
              }}
              style={{ color: '#2A3F90', size: 'small', marginRight: '16px' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box height="95%" width="100%">
            <Box
              bgcolor="white"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                overflowY:
                  isMobile || elementToRenderAux !== 'IntroductionMapComponent' ? 'auto' : 'hidden',
                overflowX: 'hidden'
              }}
            >
              <div className="container-fluid">{componentToRender}</div>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default BotonMenu;

BotonMenu.defaultProps = {
  changeLoading: () => {},
  isMobile: false,
  isPublicMap: false,
  map: {},
  MapProps: {}
};
BotonMenu.propTypes = {
  changeLoading: PropTypes.func,
  isMobile: PropTypes.bool,
  isPublicMap: PropTypes.bool,
  map: PropTypes.object,
  MapProps: PropTypes.object
};
